import AuthGuard from '../components/AuthGuard';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import toast from 'react-hot-toast';

export default function TeamPage() {
  const [stats, setStats] = useState<{ total: number; direct: number; verified: number; unverified: number }>({ total: 0, direct: 0, verified: 0, unverified: 0 });
  const [bonusBalance, setBonusBalance] = useState(0);
  const [levelCounts, setLevelCounts] = useState<[number, number, number]>([0, 0, 0]);
  const [members, setMembers] = useState<{ name: string; email: string; verified: boolean; created_at?: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [buzhuActivated, setBuzhuActivated] = useState<boolean | null>(null);
  const [showBuzhuNotice, setShowBuzhuNotice] = useState(false);
  const [showPayChannelModal, setShowPayChannelModal] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState(false);
  const [pendingPayUrl, setPendingPayUrl] = useState<string | null>(null);
  const [countdownHours, setCountdownHours] = useState<number | null>(null);
  const [isV2, setIsV2] = useState(false);

  useEffect(() => {
    api.get('/team/status').then(({ data }) => {
      setStats({ total: data.total ?? 0, direct: data.direct ?? 0, verified: data.verified ?? 0, unverified: data.unverified ?? 0 });
    }).catch(() => { });

    api.get('/me').then(({ data }) => {
      setBonusBalance(data?.accounts?.bonus ?? 0);
    }).catch(() => { });

    api.get('/bonus-activation/status').then(({ data }) => {
      setBuzhuActivated(!!data?.activated);
      if (data?.has_pending && data?.pending?.pay_url) {
        setPendingPayUrl(data.pending.pay_url);
      }
      if (data?.countdown_deadline) {
        const dl = String(data.countdown_deadline);
        const deadline = /Z|[+-]\d{2}:\d{2}/.test(dl) ? new Date(dl) : new Date(dl.replace(' ', 'T') + '+08:00');
        const ms = deadline.getTime();
        if (!isNaN(ms)) {
          const diff = Math.ceil((ms - Date.now()) / 3600000);
          setCountdownHours(Math.max(0, diff));
        }
      }
    }).catch(() => setBuzhuActivated(false));

    api.get('/limited-fund/status').then(({ data }) => setIsV2(!!data?.is_v2 || !!data?.is_v3)).catch(() => { });

    setLoading(true);
    Promise.allSettled([
      api.get('/team/members', { params: { level: 1 } }),
      api.get('/team/members', { params: { level: 2 } }),
      api.get('/team/members', { params: { level: 3 } }),
    ]).then(([r1, r2, r3]) => {
      const m1 = r1.status === 'fulfilled' ? (Array.isArray(r1.value.data.items) ? r1.value.data.items : []) : [];
      const m2 = r2.status === 'fulfilled' ? (Array.isArray(r2.value.data.items) ? r2.value.data.items : []) : [];
      const m3 = r3.status === 'fulfilled' ? (Array.isArray(r3.value.data.items) ? r3.value.data.items : []) : [];
      setLevelCounts([m1.length, m2.length, m3.length]);
      setMembers(m1);
      setLoading(false);
    });
  }, []);

  function handleWithdrawBonus() {
    if (buzhuActivated === false) {
      if (pendingPayUrl) { setPendingPrompt(true); } else { setShowBuzhuNotice(true); }
      return;
    }
    navigate('/sell?type=bonus');
  }

  async function handleBonusPay(product: string, forceNew = false) {
    if (payLoading) return;
    setPayLoading(true);
    try {
      const endpoint = import.meta.env.VITE_BONUS_PROMO_ENABLED === 'true' ? '/bonus-activation/purchase-promo' : '/bonus-activation/purchase';
      const { data } = await api.post(endpoint, { product, force_new: forceNew });
      const payUrl = data?.data?.pay_url;
      if (payUrl) {
        window.location.href = payUrl;
      } else {
        toast.error('支付链接获取失败');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? '支付创建失败');
    } finally {
      setPayLoading(false);
    }
  }

  function handleContinuePending() {
    setPendingPrompt(false);
    if (pendingPayUrl) {
      window.location.href = pendingPayUrl;
    }
  }

  function handleNewPayment() {
    setPendingPrompt(false);
    setPendingPayUrl(null);
    setShowPayChannelModal(true);
  }

  const maskPhone = (p: string) => {
    const d = (p || '').replace(/\D/g, '');
    if (d.length >= 7) return d.slice(0, 3) + '****' + d.slice(-4);
    return d || '—';
  };

  const fmtTime = (t?: string) => {
    if (!t) return '—';
    const d = new Date(t);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  return (
    <div className="max-w-md mx-auto min-h-screen bg-[#8b0000] pb-3">
      <AuthGuard />

      {/* Header */}
      <div className="relative pt-4 px-4">
        <button
          onClick={() => navigate(-1)}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-black/20 text-white mb-2 relative z-10"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
        <div className="flex flex-col items-center gap-3 justify-center relative mt-[-40px] text-white">
          <h4>我的团队</h4>
        </div>
      </div>

      {/* Stats card */}
      <div className="px-3 text-left text-gray-800 mt-6">
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          {/* Top stats: total + dividend */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="border border-gray-200 rounded-xl p-4 flex flex-col items-center gap-1">
              <span className="text-xs text-gray-500">团队总人数</span>
              <span className="text-2xl font-bold text-[#c4161c]">{stats.total}<span className="text-sm font-normal text-gray-500 ml-0.5">人</span></span>
            </div>
            <div className="border border-gray-200 rounded-xl p-4 flex flex-col items-center gap-1 cursor-pointer active:bg-gray-50" onClick={handleWithdrawBonus}>
              <span className="text-xs text-gray-500">团队分红</span>
              <span className="text-2xl font-bold text-[#c4161c]">¥{Number(bonusBalance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              {/* <span className="text-[10px] text-gray-400">点击提现 &gt;</span> */}
            </div>
          </div>

          {/* Registered count bar */}
          <div
            className="rounded-md flex items-center justify-start overflow-hidden mb-4 w-[250px]  mx-auto bg-gray-300"
          >
            <div className="flex items-center gap-2 pl-[60px] py-2">
              <span className="text-black font-bold text-sm">已注册人数</span>
            </div>
            <div className="px-4 py-1.5 ml-[40px]">
              <span className="text-[#c4161c] font-bold text-lg">{stats.direct}</span>
              <span className="text-gray-500 text-sm ml-0.5">人</span>
            </div>
          </div>

          {/* Level counts */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: '一级人数', badge: '/images/team_yi.png', count: levelCounts[0], pal: 10 },
              { label: '二级人数', badge: '/images/team_er.png', count: levelCounts[1], pal: 5 },
              { label: '三级人数', badge: '/images/team_san.png', count: levelCounts[2], pal: 2 },
            ].map(({ label, badge, count, pal }) => (
              <div key={label} className="border border-gray-200 rounded-xl p-3 flex flex-col items-center gap-2">
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-600">{label}</span>
                </div>
                <span className="text-xl font-bold text-[#c4161c]">{count}<span className="text-sm font-normal text-gray-500 ml-0.5">人</span></span>
                {/* <span className="text-sm font-normal text-[#c4161c]">{pal}<span className="text-sm font-normal text-gray-500 ml-0.5">%分红</span></span> */}
              </div>
            ))}
          </div>

          {/* Member list */}
          <div className="mt-4">
            <div className="flex items-center gap-1.5 mb-3">
              <img src="/images/team_zhuce.png" alt="" className="w-1 h-4" />
              <span className="font-bold text-sm text-gray-800">注册全部人</span>
            </div>

            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              {loading ? (
                <div className="p-8 text-center text-sm text-gray-400">加载中...</div>
              ) : members.length === 0 ? (
                <div className="p-8 text-center text-sm text-gray-400">暂无数据</div>
              ) : (
                members.map((m, idx) => (
                  <div key={idx}>
                    <div className="flex items-center gap-3 py-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{m.name || '用户名'}</div>
                        <div className="text-xs text-gray-400">{maskPhone(m.email)}</div>
                      </div>
                      <div className="text-xs text-gray-400 text-right flex-shrink-0">
                        注册时间：{fmtTime(m.created_at)}
                      </div>
                    </div>
                    {idx < members.length - 1 && <div className="mx-4 h-px bg-gray-100" />}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

      </div>


      {showBuzhuNotice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="max-w-[340px] w-[90%] relative">
            <button onClick={() => setShowBuzhuNotice(false)} className="absolute -top-3 -right-3 z-10 w-8 h-8 rounded-full bg-white text-gray-500 flex items-center justify-center shadow text-lg">&times;</button>
            <div className="relative">
              <img src={isV2 ? "/images/notice_withdraw4.png" : "/images/notice_withdraw3.png"} alt="" className="w-full rounded-xl" />
              <div className="absolute bottom-[80px] left-[40px] right-0 flex  items-center justify-between px-4">
                <div className="flex flex-col items-center gap-0 text-white text-xs">
                  <span>限时办理</span>
                  <span className="-mt-[5px]">
                    <span className="">仅剩</span>
                    <span className="text-yellow-300 font-bold text-lg mx-0.5">{countdownHours ?? 5}</span>
                    <span>小时</span>
                  </span>
                </div>
                <img
                  src={"/images/notice_withdraw3_button.png"}
                  alt="立即办理"
                  className="h-10 cursor-pointer active:opacity-80"
                  onClick={() => { setShowBuzhuNotice(false); setShowPayChannelModal(true); }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {pendingPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl mx-6 w-full max-w-sm overflow-hidden">
            <div className="px-6 pt-6 pb-4">
              <h3 className="text-lg font-semibold text-center text-gray-900">提示</h3>
              <p className="text-sm text-gray-600 text-center mt-3">
                您有一笔未完成的支付订单，是否继续完成上次支付？
              </p>
            </div>
            <div className="flex border-t border-gray-200">
              <button onClick={handleNewPayment} className="flex-1 py-3 text-sm text-gray-600 border-r border-gray-200 active:bg-gray-50">
                重新支付
              </button>
              <button onClick={handleContinuePending} className="flex-1 py-3 text-sm font-semibold text-[#c4161c] active:bg-gray-50">
                继续支付
              </button>
            </div>
          </div>
        </div>
      )}

      {showPayChannelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="max-w-md w-full bg-white rounded-2xl overflow-hidden animate-in slide-in-from-bottom duration-200">
            <div className="bg-[#c4161c] text-white text-center py-3 text-lg font-bold">选择支付方式</div>
            <div className="p-4 space-y-3">
              <button
                onClick={() => handleBonusPay('WX', !pendingPayUrl)}
                disabled={payLoading}
                className="w-full flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:bg-gray-50 active:bg-gray-100"
              >
                <img src="/images/pay_wechat.png" alt="" className="w-10 h-10" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                <span className="text-lg">微信</span>
              </button>
              <button
                onClick={() => handleBonusPay('ALI', !pendingPayUrl)}
                disabled={payLoading}
                className="w-full flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:bg-gray-50 active:bg-gray-100"
              >
                <img src="/images/pay_alipay.png" alt="" className="w-10 h-10" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                <span className="text-lg">支付宝</span>
              </button>
              <button
                onClick={() => handleBonusPay('ALI2', !pendingPayUrl)}
                disabled={payLoading}
                className="w-full flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:bg-gray-50 active:bg-gray-100"
              >
                <img src="/images/pay_alipay.png" alt="" className="w-10 h-10" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                <span className="text-lg">支付宝2</span>
              </button>
            </div>
            <div className="px-4 pb-6">
              <button onClick={() => setShowPayChannelModal(false)} className="w-full py-3 rounded-xl border border-gray-200 text-gray-600">取消</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
